import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import { useTranslation } from 'react-i18next';

import {

    flagImageUrl,

    sortLanguagesForPicker,

    resolveBrowserLocale,

} from '../i18n/languages';

import { changeLanguage as applyLanguage } from '../utils/changeLanguage';
import { useRegionalConfig } from '../utils/regionalConfig';

import '../styles/language-switcher.css';



function LangFlag({ flag, className, width = 20, height = 15 }) {

    const src = flagImageUrl(flag);

    if (!src) return null;

    return (

        <img

            className={className}

            src={src}

            alt=""

            width={width}

            height={height}

            loading="lazy"

            decoding="async"

        />

    );

}



function useCurrentLanguageCode(i18n) {

    const resolved = i18n.resolvedLanguage || i18n.language || 'ru';

    const regional = useRegionalConfig();

    const sortedLanguages = useMemo(

        () => sortLanguagesForPicker(resolveBrowserLocale()),

        [resolved, regional.languages]

    );

    const current = sortedLanguages.find((l) => l.code === resolved)?.code

        ?? sortedLanguages.find((l) => resolved.startsWith(l.code.split('-')[0]))?.code

        ?? 'ru';

    const currentLang = sortedLanguages.find((l) => l.code === current) ?? sortedLanguages[0];

    return { sortedLanguages, current, currentLang };

}



const MENU_MIN_WIDTH = 200;



/**

 * Language picker with flags.

 * variant="compact" — dropdown for landing/header (lang-btn)

 * variant="inline" — compact native select (auth forms)

 * variant="select" — full-width native select (sidebar/drawer)

 */

export default function LanguageSwitcher({ className = '', variant = 'compact', menuAlign = 'end' }) {

    const { i18n, t } = useTranslation();

    const { sortedLanguages, current, currentLang } = useCurrentLanguageCode(i18n);

    const [open, setOpen] = useState(false);

    const rootRef = useRef(null);

    const triggerRef = useRef(null);

    const menuRef = useRef(null);

    const [menuStyle, setMenuStyle] = useState(null);



    useEffect(() => {

        if (!open) return undefined;

        const onDocClick = (e) => {

            const inTrigger = rootRef.current?.contains(e.target);

            const inMenu = menuRef.current?.contains(e.target);

            if (!inTrigger && !inMenu) setOpen(false);

        };

        const onEsc = (e) => {

            if (e.key === 'Escape') setOpen(false);

        };

        document.addEventListener('mousedown', onDocClick);

        document.addEventListener('keydown', onEsc);

        return () => {

            document.removeEventListener('mousedown', onDocClick);

            document.removeEventListener('keydown', onEsc);

        };

    }, [open]);



    useLayoutEffect(() => {

        if (!open || !triggerRef.current) {

            setMenuStyle(null);

            return undefined;

        }

        const update = () => {

            const rect = triggerRef.current.getBoundingClientRect();

            let left = menuAlign === 'start' ? rect.left : rect.right - MENU_MIN_WIDTH;

            left = Math.max(8, Math.min(left, window.innerWidth - MENU_MIN_WIDTH - 8));

            setMenuStyle({

                position: 'fixed',

                top: rect.bottom + 6,

                left,

                minWidth: MENU_MIN_WIDTH,

                zIndex: 12000,

            });

        };

        update();

        window.addEventListener('resize', update);

        window.addEventListener('scroll', update, true);

        return () => {

            window.removeEventListener('resize', update);

            window.removeEventListener('scroll', update, true);

        };

    }, [open, menuAlign]);



    const changeLanguage = (code) => {
        if (!code) return;
        void applyLanguage(code);
        setOpen(false);
    };



    if (variant === 'select' || variant === 'inline') {

        return (

            <label

                className={`sp-locale-switcher sp-locale-switcher--${variant} ${className}`.trim()}

                title={t('settings:language')}

            >

                {variant === 'select' && (

                    <span className="sp-locale-switcher__icon" aria-hidden>🌐</span>

                )}

                <select

                    className="sp-locale-switcher__select"

                    value={current}

                    onChange={(e) => changeLanguage(e.target.value)}

                    aria-label={t('settings:language')}

                >

                    {sortedLanguages.map((lang) => (

                        <option key={lang.code} value={lang.code}>

                            {lang.label || lang.nativeLabel}

                        </option>

                    ))}

                </select>

            </label>

        );

    }



    return (

        <div className={`lang-switcher ${className}`.trim()} ref={rootRef}>

            <button

                ref={triggerRef}

                type="button"

                className="lang-btn"

                onClick={() => setOpen((v) => !v)}

                aria-haspopup="listbox"

                aria-expanded={open}

                aria-label={t('settings:language')}

                title={currentLang?.label || currentLang?.nativeLabel}

            >

                <LangFlag flag={currentLang?.flag} className="lang-btn__flag" />

                <span className="lang-btn__code">{currentLang?.shortCode}</span>

                <span className="lang-btn__chevron" aria-hidden>▾</span>

            </button>

            {open && menuStyle && createPortal(

                <div

                    ref={menuRef}

                    className="lang-menu"

                    style={menuStyle}

                    role="listbox"

                    aria-label={t('settings:language')}

                >

                    {sortedLanguages.map((lang) => {

                        const isActive = lang.code === current;

                        return (

                            <button

                                key={lang.code}

                                type="button"

                                role="option"

                                aria-selected={isActive}

                                className={`lang-item${isActive ? ' lang-item--active' : ''}`}

                                onClick={() => changeLanguage(lang.code)}

                            >

                                <LangFlag flag={lang.flag} className="lang-item__flag" width={22} height={16} />

                                <span className="lang-item__label">{lang.label || lang.nativeLabel}</span>

                                {isActive && <span className="lang-item__check" aria-hidden>✓</span>}

                            </button>

                        );

                    })}

                </div>,

                document.body,

            )}

        </div>

    );

}


